import AddIcon from '@mui/icons-material/Add';
import { Button, Chip, DialogContent, Grid, Stack } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { createInvoiceType, updateInvoiceType } from 'wisestep-api';
import {
  ControlledTextInput,
  WsButton,
  WsDialog,
  WsSelect,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import WsCheckBox from '../../../components/WsCheckBox/WsCheckBox';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { HeaderBox } from '../../jobSettings/components/HeaderBox';
import { invoiceResetPeriods, monthOptions, placeholders } from '../helper/InvoiceTypeHelper';

interface IAddEditInvoiceTypeDialog {
  defaultValues?: any;
  onCancel(): void;
  onSave(data?): void;
}

const invoiceTypeDefaults = {
  resetStartFrom: null,
  resetEvery: '',
  sequenceStartFrom: 1,
  allowContracts: true,
  allowTaxes: true,
  allowAdjustments: true,
};

const AddEditInvoiceTypeDialog = ({
  defaultValues,
  onCancel,
  onSave,
}: IAddEditInvoiceTypeDialog) => {
  const methods = useForm({ defaultValues: defaultValues ?? invoiceTypeDefaults });
  const setNotification = useNotificationStore((state) => state.setNotification);

  const invNumFormatString = methods.watch('format') ?? '';

  let exampleInvFormat = invNumFormatString;
  placeholders.forEach((placeholder) => {
    exampleInvFormat = exampleInvFormat.replaceAll(`{{${placeholder.value}}}`, placeholder.example);
  });
  const edit = !!methods.getValues('id');

  const handleSave = async (data) => {
    try {
      if (defaultValues?.id) {
        await updateInvoiceType(data);
        setNotification(success('Invoice Type has been updated successfully !!'));
      } else {
        const response = await createInvoiceType(data);
        data = response.data;
        setNotification(success('Invoice Type has been added successfully !!'));
      }
      onSave(data);
    } catch {
      setNotification(failed('Something went wrong , Please try again !!'));
    }
  };

  return (
    <FormProvider {...methods}>
      <WsDialog openDialog={true} size="small">
        <DialogContent>
          <HeaderBox>
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
              {defaultValues?.id ? 'Edit' : 'Add'} Invoice Type
            </WsTypography>
          </HeaderBox>
          <form onSubmit={methods.handleSubmit(handleSave)}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <ControlledTextInput
                  fullWidth
                  required
                  labelText="Invoice Type *"
                  name="name"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ControlledTextInput
                  fullWidth
                  name="sequenceStartFrom"
                  labelText="Start Sequence"
                  size="small"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <WsSelect
                  label="Invoice Reset Period"
                  name="resetEvery"
                  selectArray={invoiceResetPeriods}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <WsSelect label="Month" name="resetStartFrom" selectArray={monthOptions} />
              </Grid>
              <Grid item xs={12}>
                <Stack>
                  <WsCheckBox name="allowContracts" label="Allow Contracts" />
                  <WsCheckBox name="allowTaxes" label="Allow Taxes" />
                  <WsCheckBox name="allowAdjustments" label="Allow Adjustments" />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <ControlledTextInput
                    fullWidth
                    labelText="Invoice Number Format *"
                    name="format"
                    size="small"
                    required
                  />
                  <Stack direction="row" spacing={1}>
                    {placeholders.map((p) => (
                      <Chip
                        key={p.value}
                        icon={<AddIcon fontSize="small" />}
                        style={{ borderRadius: 4 }}
                        onClick={() =>
                          methods.setValue('format', `${invNumFormatString}{{${p.value}}}`)
                        }
                        label={p.label}
                      />
                    ))}
                  </Stack>
                  <WsTypography variant={WsTypoVariants.CAPTION}>
                    Example: {exampleInvFormat}
                  </WsTypography>
                </Stack>
              </Grid>
              {!edit && (
                <Grid item xs={12}>
                  <WsCheckBox name="default" label="Make Default" />
                </Grid>
              )}
            </Grid>
            <Stack mt={4} direction="row" justifyContent="flex-end" spacing={2}>
              <Button size="large" color="error" onClick={() => onCancel()}>
                Cancel
              </Button>
              <WsButton size="large" type="submit">
                Save
              </WsButton>
            </Stack>
          </form>
        </DialogContent>
      </WsDialog>
    </FormProvider>
  );
};

export default AddEditInvoiceTypeDialog;
